import React, { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext();

// Helper function to extract numeric price safely
const extractNumericPrice = (price) => {
  // Debug logging
  console.log('🔍 extractNumericPrice called with:', {
    price,
    type: typeof price,
    isString: typeof price === 'string',
    isNumber: typeof price === 'number'
  });
  
  if (price === null || price === undefined) {
    console.log('  -> Price is null/undefined, returning 0');
    return 0;
  }
  
  if (typeof price === 'number') {
    console.log('  -> Price is already a number:', price);
    return price;
  }
  
  if (typeof price === 'string') {
    // Remove any non-numeric characters except dots and minus signs
    const numericValue = parseFloat(price.replace(/[^0-9.-]+/g, ''));
    console.log('  -> Extracted from string:', numericValue);
    return isNaN(numericValue) ? 0 : numericValue;
  }
  
  // Try to convert other types to number
  try {
    const numericValue = parseFloat(price);
    console.log('  -> Parsed other type:', numericValue);
    return isNaN(numericValue) ? 0 : numericValue;
  } catch (error) {
    console.log('  -> Error parsing price, returning 0');
    return 0;
  }
};

const cartReducer = (state, action) => {
  console.log('🔄 cartReducer called with action:', action.type, action.payload);
  
  switch (action.type) {
    case 'ADD_TO_CART':
      console.log('📦 Adding to cart:', action.payload);
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        const newState = {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        };
        console.log('✅ Updated existing item quantity. New state:', newState);
        return newState;
      }
      const newItemState = {
        ...state,
        items: [...state.items, { ...action.payload, quantity: 1 }]
      };
      console.log('✅ Added new item. New state:', newItemState);
      return newItemState;

    case 'REMOVE_FROM_CART':
      console.log('🗑️ Removing from cart:', action.payload);
      const removedState = {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
      console.log('✅ Item removed. New state:', removedState);
      return removedState;

    case 'UPDATE_QUANTITY':
      console.log('📊 Updating quantity:', action.payload);
      const updatedState = {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };
      console.log('✅ Quantity updated. New state:', updatedState);
      return updatedState;

    case 'CLEAR_CART':
      console.log('🧹 Clearing cart');
      return {
        ...state,
        items: []
      };

    default:
      return state;
  }
};

const initialState = {
  items: []
};


export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Debug effect to log cart state changes
  useEffect(() => {
    console.log('🛒 Cart state updated:', state);
    console.log('📋 Items in cart:', state.items.length);
    
    if (state.items.length > 0) {
      console.log('🔍 Detailed item analysis:');
      state.items.forEach((item, index) => {
        console.log(`  Item ${index + 1}:`, {
          id: item.id,
          name: item.name,
          price: item.price,
          priceType: typeof item.price,
          priceRawValue: item.price,
          quantity: item.quantity,
          // Test the extraction
          extractedPrice: extractNumericPrice(item.price)
        });
      });
    }
  }, [state]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(state.items));
  }, [state.items]);

  // Save cart to user profile if user is logged in
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user && state.items.length > 0) {
      // Save to user's profile in localStorage
      const userProfileKey = `user_${user.id}_cart`;
      localStorage.setItem(userProfileKey, JSON.stringify(state.items));
    }
  }, [state.items]);

  // Load user's cart when they log in
  const loadUserCart = (userId) => {
    const userProfileKey = `user_${userId}_cart`;
    const savedUserCart = localStorage.getItem(userProfileKey);
    if (savedUserCart) {
      try {
        const cartData = JSON.parse(savedUserCart);
        // Clear current cart and load user's cart
        dispatch({ type: 'CLEAR_CART' });
        cartData.forEach(item => {
          dispatch({ type: 'ADD_TO_CART', payload: item });
        });
      } catch (error) {
        console.error('Error loading user cart:', error);
      }
    }
  };

  // Merge guest cart with user cart when logging in
  const mergeGuestCartWithUserCart = (userId) => {
    const guestCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const userProfileKey = `user_${userId}_cart`;
    const userCart = JSON.parse(localStorage.getItem(userProfileKey) || '[]');
    
    // Merge carts (combine quantities for same items)
    const mergedCart = [...userCart];
    
    guestCart.forEach(guestItem => {
      const existingUserItem = mergedCart.find(userItem => userItem.id === guestItem.id);
      if (existingUserItem) {
        existingUserItem.quantity += guestItem.quantity;
      } else {
        mergedCart.push(guestItem);
      }
    });
    
    // Save merged cart to user profile
    localStorage.setItem(userProfileKey, JSON.stringify(mergedCart));
    
    // Clear guest cart
    localStorage.removeItem('cart');
    
    // Load merged cart into state
    dispatch({ type: 'CLEAR_CART' });
    mergedCart.forEach(item => {
      dispatch({ type: 'ADD_TO_CART', payload: item });
    });
  };

  const addToCart = (product) => {
    console.log('➕ addToCart called with product:', product);
    dispatch({ type: 'ADD_TO_CART', payload: product });
  };

  const removeFromCart = (productId) => {
    console.log('➖ removeFromCart called for productId:', productId);
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    console.log('🔄 updateQuantity called:', { productId, quantity });
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
    }
  };

  const clearCart = () => {
    console.log('🧹 clearCart called');
    dispatch({ type: 'CLEAR_CART' });
  };

  const getCartTotal = () => {
    console.log('💰 getCartTotal called');
    console.log('📊 Items to process:', state.items.length);
    
    const total = state.items.reduce((total, item, index) => {
      const numericPrice = extractNumericPrice(item.price);
      const itemTotal = numericPrice * item.quantity;
      
      console.log(`  Item ${index + 1}: ${item.name}`);
      console.log(`    Raw price: ${item.price}`);
      console.log(`    Type: ${typeof item.price}`);
      console.log(`    Numeric price: ${numericPrice}`);
      console.log(`    Quantity: ${item.quantity}`);
      console.log(`    Item total: ${itemTotal}`);
      console.log(`    Running total: ${total + itemTotal}`);
      
      return total + itemTotal;
    }, 0);
    
    console.log(`✅ Final cart total: ${total}`);
    return total;
  };

  const getCartItemsCount = () => {
    const count = state.items.reduce((total, item) => total + item.quantity, 0);
    console.log('📦 getCartItemsCount:', count);
    return count;
  };




  return (
    <CartContext.Provider value={{
      cart: state,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartItemsCount,
      loadUserCart,
      mergeGuestCartWithUserCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
